import React, { useState, useCallback, useEffect } from 'react';
import { collection, addDoc, getDocs, doc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase/config.ts';
import { useAuth } from '../contexts/AuthContext.tsx';
import { Employee } from '../types/employee.ts';
import { Upload, Download, FileText, CheckCircle, AlertCircle, User, DollarSign, Building } from 'lucide-react';
import { useDropzone } from 'react-dropzone';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';

interface GustoEmployeeData {
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  jobTitle: string;
  hireDate: Date;
  terminationDate?: Date | null;
  status: 'active' | 'terminated';
  hourlyRate: number;
  streetAddress1?: string;
  streetAddress2?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  dateOfBirth?: Date | null;
  ssn?: string;
  payType?: string;
  department?: string;
}

interface ImportableEmployee {
  gustoData: GustoEmployeeData;
  isSelected: boolean;
  existsInFirebase: boolean;
  firebaseId?: string;
  conflictResolution: 'skip' | 'update' | 'create_new';
}

// Gusto CSV column mappings based on actual export format
const GUSTO_COLUMN_MAPPINGS = {
  // Name fields - exact matches from your Gusto export
  firstName: ['First', 'First Name', 'first_name', 'FirstName', 'Employee First Name', 'First name (preferred)'],
  lastName: ['Employee', 'Last Name', 'last_name', 'LastName', 'Employee Last Name'],
  
  // Contact - exact matches
  email: ['Email address', 'Email', 'email', 'Email Address', 'Work Email', 'Personal Email'],
  phone: ['Phone number', 'Phone', 'phone', 'Phone Number', 'Mobile Phone', 'Personal Phone'],
  
  // Job info
  jobTitle: ['Job Title', 'job_title', 'Title', 'Position', 'Role'],
  department: ['Department', 'department', 'Division', 'Team'],
  
  // Dates - exact matches from your export
  hireDate: ['Employee start date', 'Hire Date', 'hire_date', 'Start Date', 'Employment Start Date'],
  terminationDate: ['Employee end date', 'Termination Date', 'termination_date', 'End Date', 'Employment End Date'],
  dateOfBirth: ['Date of birth', 'Date of Birth', 'date_of_birth', 'DOB', 'Birth Date'],
  
  // Compensation - exact match
  hourlyRate: ['Regular rate (hourly)', 'Hourly Rate', 'hourly_rate', 'Rate', 'Pay Rate', 'Wage', 'Current Rate'],
  salary: ['Salary', 'salary', 'Annual Salary', 'Yearly Salary'],
  payType: ['Pay Type', 'pay_type', 'Payment Type', 'Compensation Type'],
  
  // Address
  streetAddress1: ['Street Address', 'street_address_1', 'Address 1', 'Address Line 1', 'Street 1'],
  streetAddress2: ['Street Address 2', 'street_address_2', 'Address 2', 'Address Line 2', 'Street 2'],
  city: ['City', 'city'],
  state: ['State', 'state', 'Province'],
  zipCode: ['Zip Code', 'zip_code', 'ZIP', 'Postal Code'],
  
  // Status
  status: ['Status', 'status', 'Employment Status', 'Employee Status'],
  
  // SSN
  ssn: ['SSN', 'ssn', 'Social Security Number', 'Social Security']
};

const GustoCSVImport: React.FC = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // CSV data
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [csvHeaders, setCsvHeaders] = useState<string[]>([]);
  const [rawData, setRawData] = useState<any[]>([]);
  const [employees, setEmployees] = useState<ImportableEmployee[]>([]);
  const [existingEmployees, setExistingEmployees] = useState<Employee[]>([]);
  const [showPreview, setShowPreview] = useState(false);
  const [showMapping, setShowMapping] = useState(false);
  const [columnMapping, setColumnMapping] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    loadExistingEmployees();
  }, []);

  const loadExistingEmployees = async () => {
    try {
      const employeesRef = collection(db, 'employees');
      const snapshot = await getDocs(employeesRef);
      
      const employeeData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate() || new Date(),
        updatedAt: doc.data().updatedAt?.toDate() || new Date(),
        hireDate: doc.data().hireDate?.toDate(),
        terminationDate: doc.data().terminationDate?.toDate(),
        licenseExpiration: doc.data().licenseExpiration?.toDate(),
      })) as Employee[];

      setExistingEmployees(employeeData);
    } catch (err) {
      console.error('Error loading existing employees:', err);
    }
  };

  const detectGustoColumns = (headers: string[]): { [key: string]: string } => {
    const mapping: { [key: string]: string } = {};
    
    console.log('Available headers:', headers); // Debug log
    
    Object.entries(GUSTO_COLUMN_MAPPINGS).forEach(([field, possibleColumns]) => {
      const matchedColumn = headers.find(header => 
        possibleColumns.some(possible => 
          header.trim().toLowerCase() === possible.trim().toLowerCase()
        )
      );
      
      if (matchedColumn) {
        mapping[field] = matchedColumn;
        console.log(`Mapped ${field} to ${matchedColumn}`); // Debug log
      }
    });
    
    console.log('Final mapping:', mapping); // Debug log
    return mapping;
  };

  const processCSVFile = useCallback((file: File) => {
    setLoading(true);
    setError(null);
    setCsvFile(file);
    
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const text = e.target?.result as string;
        const workbook = XLSX.read(text, { type: 'string' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { raw: false });
        
        if (jsonData.length === 0) {
          setError('No data found in CSV file');
          setLoading(false);
          return;
        }
        
        const headers = Object.keys(jsonData[0] as Record<string, any>);
        setCsvHeaders(headers);
        setRawData(jsonData);
        
        // Auto-detect Gusto columns
        const detectedMapping = detectGustoColumns(headers);
        setColumnMapping(detectedMapping);
        
        // Show mapping interface
        setShowMapping(true);
        setLoading(false);
        
      } catch (err) {
        console.error('Error processing CSV:', err);
        setError(`Error processing CSV file: ${err}`);
        setLoading(false);
      }
    };
    
    reader.onerror = () => {
      setError('Error reading CSV file');
      setLoading(false);
    };
    
    reader.readAsText(file);
  }, []);

  const generatePreview = () => {
    try {
      setLoading(true);
      
      // Process the data with current mapping
      const importableEmployees: ImportableEmployee[] = rawData.map((row: any) => {
        const gustoData = transformRowToEmployeeData(row, columnMapping);
        
        // Check if employee already exists
        const existingEmployee = existingEmployees.find(emp => 
          (emp.email && gustoData.email && emp.email.toLowerCase() === gustoData.email.toLowerCase()) ||
          (emp.firstName && emp.lastName && 
           emp.firstName.toLowerCase() === gustoData.firstName.toLowerCase() && 
           emp.lastName.toLowerCase() === gustoData.lastName.toLowerCase())
        );

        return {
          gustoData,
          isSelected: !existingEmployee, // Select new employees by default
          existsInFirebase: !!existingEmployee,
          firebaseId: existingEmployee?.id,
          conflictResolution: existingEmployee ? 'update' : 'create_new'
        };
      });

      setEmployees(importableEmployees);
      setShowMapping(false);
      setShowPreview(true);
      setLoading(false);
      
    } catch (err) {
      console.error('Error generating preview:', err);
      setError(`Error processing data: ${err}`);
      setLoading(false);
    }
  };

  const transformRowToEmployeeData = (row: any, mapping: { [key: string]: string }): GustoEmployeeData => {
    const parseDate = (dateStr: string): Date | null => {
      if (!dateStr) return null;
      const date = new Date(dateStr);
      return isNaN(date.getTime()) ? null : date;
    };

    const parseRate = (rateStr: string, payType?: string): number => {
      if (!rateStr) return 0;
      const rate = parseFloat(rateStr.replace(/[,$]/g, ''));
      if (isNaN(rate)) return 0;
      
      // Convert salary to hourly if needed
      if (payType?.toLowerCase().includes('salary') || payType?.toLowerCase().includes('annual')) {
        return rate / (40 * 52); // Assume 40 hours/week, 52 weeks/year
      }
      
      return rate;
    };

    const status = row[mapping.status] || 'active';
    const terminationDate = mapping.terminationDate ? parseDate(row[mapping.terminationDate]) : null;
    
    return {
      firstName: row[mapping.firstName] || '',
      lastName: row[mapping.lastName] || '',
      email: row[mapping.email] || '',
      phone: row[mapping.phone] || '',
      jobTitle: row[mapping.jobTitle] || 'Employee',
      hireDate: parseDate(row[mapping.hireDate]) || new Date(),
      terminationDate,
      status: terminationDate ? 'terminated' : 
             (status.toLowerCase().includes('terminated') || status.toLowerCase().includes('inactive') ? 'terminated' : 'active'),
      hourlyRate: parseRate(row[mapping.hourlyRate] || row[mapping.salary], row[mapping.payType]),
      streetAddress1: row[mapping.streetAddress1] || '',
      streetAddress2: row[mapping.streetAddress2] || '',
      city: row[mapping.city] || '',
      state: row[mapping.state] || '',
      zipCode: row[mapping.zipCode] || '',
      dateOfBirth: mapping.dateOfBirth ? parseDate(row[mapping.dateOfBirth]) : null,
      ssn: row[mapping.ssn] || '',
      payType: row[mapping.payType] || '',
      department: row[mapping.department] || ''
    };
  };

  const toggleEmployeeSelection = (index: number) => {
    setEmployees(prev => prev.map((emp, i) => 
      i === index ? { ...emp, isSelected: !emp.isSelected } : emp
    ));
  };

  const updateConflictResolution = (index: number, resolution: 'skip' | 'update' | 'create_new') => {
    setEmployees(prev => prev.map((emp, i) => 
      i === index ? { ...emp, conflictResolution: resolution } : emp
    ));
  };

  const importSelectedEmployees = async () => {
    if (!currentUser) return;

    const selectedEmployees = employees.filter(emp => emp.isSelected);
    if (selectedEmployees.length === 0) {
      setError('Please select at least one employee to import');
      return;
    }

    try {
      setImporting(true);
      setError(null);
      
      let importedCount = 0;
      let updatedCount = 0;
      let skippedCount = 0;

      for (const importableEmployee of selectedEmployees) {
        const { gustoData, existsInFirebase, firebaseId, conflictResolution } = importableEmployee;

        if (existsInFirebase && conflictResolution === 'skip') {
          skippedCount++;
          continue;
        }

        // Transform to Firebase Employee format
        const employeeData = {
          firstName: gustoData.firstName,
          lastName: gustoData.lastName,
          name: `${gustoData.firstName} ${gustoData.lastName}`, // Legacy support
          email: gustoData.email,
          phone: gustoData.phone,
          jobTitle: gustoData.jobTitle,
          hireDate: gustoData.hireDate,
          terminationDate: gustoData.terminationDate,
          hourlyWage: gustoData.hourlyRate,
          rates: {
            cec: gustoData.hourlyRate, // Default to CEC rate
            reliable: 0,
            btc: 0
          },
          status: gustoData.status,
          address: gustoData.streetAddress1 ? {
            street1: gustoData.streetAddress1,
            street2: gustoData.streetAddress2 || '',
            city: gustoData.city,
            state: gustoData.state,
            zipCode: gustoData.zipCode
          } : null,
          dateOfBirth: gustoData.dateOfBirth,
          importedFromGusto: true,
          lastGustoSync: new Date(),
          createdAt: existsInFirebase ? undefined : new Date(),
          updatedAt: new Date(),
          createdBy: currentUser.uid,
          // Optional fields with defaults
          burdenedRate: gustoData.hourlyRate * 1.3, // Default burden multiplier
          fica: 0,
          insurance: 0,
          vacation: 0,
          markup: 0,
          application: true
        };

        if (existsInFirebase && conflictResolution === 'update' && firebaseId) {
          // Update existing employee
          await updateDoc(doc(db, 'employees', firebaseId), {
            ...employeeData,
            createdAt: undefined // Don't update createdAt
          });
          updatedCount++;
        } else {
          // Create new employee
          await addDoc(collection(db, 'employees'), employeeData);
          importedCount++;
        }
      }

      setSuccess(`Import completed! Created: ${importedCount}, Updated: ${updatedCount}, Skipped: ${skippedCount}`);
      
      // Refresh existing employees list
      await loadExistingEmployees();
      
      // Reset state
      setShowPreview(false);
      setEmployees([]);
      setCsvFile(null);
      
    } catch (err) {
      console.error('Error importing employees:', err);
      setError(err instanceof Error ? err.message : 'Failed to import employees');
    } finally {
      setImporting(false);
    }
  };

  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      processCSVFile(acceptedFiles[0]);
    }
  }, [processCSVFile]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'text/csv': ['.csv'],
      'application/vnd.ms-excel': ['.xls'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx']
    }
  });

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="mb-8">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Import from Gusto CSV</h1>
            <p className="text-gray-600 mt-1">
              Import employee data from a Gusto CSV export
            </p>
          </div>
          <button
            onClick={() => navigate('/employees')}
            className="px-4 py-2 text-gray-600 hover:text-gray-800 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
          >
            Back to Employees
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6 flex items-center">
          <AlertCircle className="h-5 w-5 mr-2" />
          {error}
          <button onClick={() => setError(null)} className="ml-auto">×</button>
        </div>
      )}

      {success && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-6 flex items-center">
          <CheckCircle className="h-5 w-5 mr-2" />
          {success}
          <button onClick={() => setSuccess(null)} className="ml-auto">×</button>
        </div>
      )}

      {!showPreview && (
        <div className="bg-white shadow-xl rounded-xl overflow-hidden mb-8">
          <div className="bg-gradient-to-r from-purple-600 to-indigo-700 px-8 py-6 text-white">
            <h2 className="text-2xl font-bold text-center mb-2">Upload Gusto CSV Export</h2>
            <p className="text-center text-purple-100">
              Upload a CSV file exported from your Gusto dashboard
            </p>
          </div>
          
          <div className="p-8">
            {/* Instructions */}
            <div className="mb-6 bg-blue-50 rounded-lg p-4">
              <h3 className="text-lg font-medium text-gray-900 mb-3">How to Export from Gusto:</h3>
              <ol className="list-decimal list-inside space-y-2 text-gray-700">
                <li>Log into your Gusto dashboard</li>
                <li>Go to Reports → People Reports</li>
                <li>Select "Employee Directory" or "Payroll Details" report</li>
                <li>Export as CSV</li>
                <li>Upload the CSV file below</li>
              </ol>
            </div>

            <div 
              {...getRootProps()} 
              className="border-2 border-dashed border-purple-300 rounded-xl p-10 text-center cursor-pointer transition-all duration-300 hover:border-purple-500 hover:bg-purple-50"
            >
              <input {...getInputProps()} />
              <div className="flex flex-col items-center">
                <Upload className={`h-16 w-16 mb-4 ${isDragActive ? 'text-purple-600' : 'text-purple-400'}`} />
                {isDragActive ? (
                  <p className="text-purple-600 text-lg font-medium">Drop the CSV file here...</p>
                ) : (
                  <>
                    <p className="text-gray-700 text-lg font-medium mb-2">Drag and drop your Gusto CSV export here</p>
                    <p className="text-gray-500">or click to select a file</p>
                    <p className="text-gray-400 text-sm mt-2">Supports .csv, .xls, and .xlsx files</p>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Manual Column Mapping Interface */}
      {showMapping && !loading && (
        <div className="bg-white shadow-xl rounded-xl overflow-hidden mb-8">
          <div className="bg-gradient-to-r from-blue-600 to-indigo-700 px-8 py-6 text-white">
            <h2 className="text-2xl font-bold text-center mb-2">Map CSV Columns</h2>
            <p className="text-center text-blue-100">
              Review and adjust the column mapping for your Gusto data
            </p>
          </div>
          
          <div className="p-8">
            <div className="mb-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <h3 className="text-sm font-medium text-yellow-800 mb-2">Detected Columns:</h3>
              <div className="flex flex-wrap gap-2">
                {csvHeaders.map(header => (
                  <span key={header} className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded-md text-xs">
                    {header}
                  </span>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              {/* Required Fields */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Required Fields</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      First Name *
                    </label>
                    <select
                      value={columnMapping.firstName || ''}
                      onChange={(e) => setColumnMapping({...columnMapping, firstName: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select column...</option>
                      {csvHeaders.map(header => (
                        <option key={header} value={header}>
                          {header}
                          {rawData[0] && rawData[0][header] ? ` (e.g., "${rawData[0][header]}")` : ''}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Last Name *
                    </label>
                    <select
                      value={columnMapping.lastName || ''}
                      onChange={(e) => setColumnMapping({...columnMapping, lastName: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select column...</option>
                      {csvHeaders.map(header => (
                        <option key={header} value={header}>
                          {header}
                          {rawData[0] && rawData[0][header] ? ` (e.g., "${rawData[0][header]}")` : ''}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Hourly Rate *
                    </label>
                    <select
                      value={columnMapping.hourlyRate || ''}
                      onChange={(e) => setColumnMapping({...columnMapping, hourlyRate: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select column...</option>
                      {csvHeaders.map(header => (
                        <option key={header} value={header}>
                          {header}
                          {rawData[0] && rawData[0][header] ? ` (e.g., "${rawData[0][header]}")` : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Optional Fields */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Optional Fields</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email
                    </label>
                    <select
                      value={columnMapping.email || ''}
                      onChange={(e) => setColumnMapping({...columnMapping, email: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select column...</option>
                      {csvHeaders.map(header => (
                        <option key={header} value={header}>
                          {header}
                          {rawData[0] && rawData[0][header] ? ` (e.g., "${rawData[0][header]}")` : ''}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Phone
                    </label>
                    <select
                      value={columnMapping.phone || ''}
                      onChange={(e) => setColumnMapping({...columnMapping, phone: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select column...</option>
                      {csvHeaders.map(header => (
                        <option key={header} value={header}>
                          {header}
                          {rawData[0] && rawData[0][header] ? ` (e.g., "${rawData[0][header]}")` : ''}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Hire Date
                    </label>
                    <select
                      value={columnMapping.hireDate || ''}
                      onChange={(e) => setColumnMapping({...columnMapping, hireDate: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select column...</option>
                      {csvHeaders.map(header => (
                        <option key={header} value={header}>
                          {header}
                          {rawData[0] && rawData[0][header] ? ` (e.g., "${rawData[0][header]}")` : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </div>

            {/* Sample Data Preview */}
            {rawData.length > 0 && (
              <div className="mb-6 bg-gray-50 rounded-lg p-4">
                <h3 className="text-sm font-medium text-gray-900 mb-2">Sample Data (First Row):</h3>
                <div className="text-xs">
                  {csvHeaders.map(header => (
                    <div key={header} className="flex justify-between py-1 border-b border-gray-200 last:border-b-0">
                      <span className="font-medium">{header}:</span>
                      <span className="text-gray-600">{rawData[0][header] || '(empty)'}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex justify-end space-x-4">
              <button
                onClick={() => {
                  setShowMapping(false);
                  setCsvFile(null);
                  setCsvHeaders([]);
                  setRawData([]);
                }}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={generatePreview}
                disabled={!columnMapping.firstName || !columnMapping.lastName}
                className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Generate Preview
              </button>
            </div>
          </div>
        </div>
      )}

      {showPreview && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                Gusto Employee Data Preview
              </h2>
              <p className="text-gray-600">
                {employees.length} employees found • {employees.filter(e => e.isSelected).length} selected for import
              </p>
            </div>
            <div className="flex space-x-3">
              <button
                onClick={() => {
                  setShowPreview(false);
                  setShowMapping(true);
                }}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
              >
                Back to Mapping
              </button>
              <button
                onClick={importSelectedEmployees}
                disabled={importing || employees.filter(e => e.isSelected).length === 0}
                className="inline-flex items-center px-6 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {importing ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Importing...
                  </>
                ) : (
                  <>
                    <Download className="h-4 w-4 mr-2" />
                    Import Selected ({employees.filter(e => e.isSelected).length})
                  </>
                )}
              </button>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <input
                      type="checkbox"
                      checked={employees.every(e => e.isSelected)}
                      onChange={(e) => {
                        const allSelected = e.target.checked;
                        setEmployees(prev => prev.map(emp => ({ ...emp, isSelected: allSelected })));
                      }}
                      className="h-4 w-4 text-purple-600 rounded border-gray-300"
                    />
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Employee</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Job Title</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Hourly Rate</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Hire Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {employees.map((employee, index) => (
                  <tr key={index} className={employee.existsInFirebase ? 'bg-yellow-50' : ''}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <input
                        type="checkbox"
                        checked={employee.isSelected}
                        onChange={() => toggleEmployeeSelection(index)}
                        className="h-4 w-4 text-purple-600 rounded border-gray-300"
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <User className="h-5 w-5 text-gray-400 mr-2" />
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {employee.gustoData.firstName} {employee.gustoData.lastName}
                          </div>
                          <div className="text-sm text-gray-500">{employee.gustoData.email}</div>
                          {employee.existsInFirebase && (
                            <div className="text-xs text-yellow-600">⚠️ Already exists in system</div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div className="flex items-center">
                        <Building className="h-4 w-4 text-gray-400 mr-1" />
                        {employee.gustoData.jobTitle}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <DollarSign className="h-4 w-4 text-gray-400 mr-1" />
                        <span className="text-sm font-medium text-gray-900">
                          ${employee.gustoData.hourlyRate.toFixed(2)}/hr
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {employee.gustoData.hireDate.toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        employee.gustoData.status === 'active' 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {employee.gustoData.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {employee.existsInFirebase ? (
                        <select
                          value={employee.conflictResolution}
                          onChange={(e) => updateConflictResolution(index, e.target.value as any)}
                          className="text-xs border border-gray-300 rounded px-2 py-1"
                        >
                          <option value="update">Update existing</option>
                          <option value="skip">Skip</option>
                          <option value="create_new">Create new</option>
                        </select>
                      ) : (
                        <span className="text-green-600 text-xs">Will create new</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {loading && (
        <div className="flex justify-center items-center my-8 bg-white p-8 rounded-xl shadow-lg">
          <div className="flex flex-col items-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500 mb-4"></div>
            <span className="text-purple-600 text-lg font-medium">Processing CSV file...</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default GustoCSVImport;